import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/* Local primitives instead of @leopardaelectric/vammo-ui.
   The package lives in GitHub Packages and the cactus build has no token for it: the
   platform recipe dies on `401 Unauthorized @leopardaelectric/vammo-ui` and a Dockerfile
   build gets VAMMO_NPM_TOKEN empty (both confirmed in the build logs). battery-activator,
   the reference SPA for this deploy, carries no vammo-ui either.
   The visual language still comes from the design system: every colour below is a token
   from the @theme block in index.css, which is copied from vammo-spa-template. */

const cn = (...parts: Array<string | false | undefined>): string =>
  parts.filter((part): part is string => Boolean(part)).join(' ');

type ButtonVariant = 'primary' | 'accent' | 'outline' | 'ghost' | 'success';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant };

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg text-base font-bold transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ' +
  'disabled:pointer-events-none disabled:opacity-50';

const BUTTON_VARIANT = {
  primary: 'bg-black text-neon-yellow hover:bg-grey-800',
  /* The inverse of primary, for a secondary action that still has to read as one. Neon
     because this page is read on a phone: the lead scrolls past the answer, and a black
     button blends into a column of black text and dark controls while this one does not.
     Black on neon clears WCAG AAA. */
  accent: 'bg-neon-yellow text-black hover:brightness-95',
  outline: 'border border-grey-400 bg-white text-black hover:bg-grey-100',
  ghost: 'text-grey-800 hover:bg-grey-100',
  success: 'bg-green-500 text-white hover:bg-green-700',
} satisfies Record<ButtonVariant, string>;

export const Button = ({ variant = 'primary', className, ...props }: ButtonProps) => (
  <button className={cn(BUTTON_BASE, BUTTON_VARIANT[variant], className)} {...props} />
);

export const buttonClasses = (variant: ButtonVariant, className?: string): string =>
  cn(BUTTON_BASE, BUTTON_VARIANT[variant], className);

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean;
  errorMessage?: string | false;
};

export const Input = ({ hasError, errorMessage, className, id, ...props }: InputProps) => {
  const invalid = hasError === true || Boolean(errorMessage);
  const errorId = invalid && id ? `${id}-error` : undefined;
  return (
    <>
      <input
        id={id}
        aria-invalid={invalid}
        aria-describedby={errorId}
        className={cn(
          'w-full rounded-lg border bg-white px-3 py-1 text-base text-black transition-colors',
          'placeholder:text-grey-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black',
          invalid ? 'border-red-500' : 'border-grey-400',
          className,
        )}
        {...props}
      />
      {errorMessage ? (
        <p id={errorId} role="alert" aria-live="polite" className="text-xs text-red-500">
          {errorMessage}
        </p>
      ) : null}
    </>
  );
};

export const Label = ({ className, ...props }: HTMLAttributes<HTMLLabelElement> & { htmlFor?: string }) => (
  <label className={cn('font-bold text-black', className)} {...props} />
);

export const Card = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('rounded-xl border border-grey-200 bg-white shadow-sm', className)} {...props} />
);

export const CardContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5', className)} {...props} />
);

type BadgeVariant = 'green' | 'blue' | 'yellow' | 'red' | 'grey';

const BADGE_VARIANT = {
  green: 'bg-green-500 text-white',
  blue: 'bg-blue-500 text-white',
  yellow: 'bg-yellow-500 text-black',
  red: 'bg-red-500 text-white',
  grey: 'bg-grey-200 text-grey-800',
} satisfies Record<BadgeVariant, string>;

type BadgeProps = HTMLAttributes<HTMLSpanElement> & { variant?: BadgeVariant };

export const Badge = ({ variant = 'grey', className, ...props }: BadgeProps) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
      BADGE_VARIANT[variant],
      className,
    )}
    {...props}
  />
);

type AlertVariant = 'warning' | 'destructive';

const ALERT_VARIANT = {
  warning: 'border-yellow-500 bg-yellow-300 text-black',
  destructive: 'border-red-500 bg-red-300 text-red-700',
} satisfies Record<AlertVariant, string>;

type AlertProps = { variant: AlertVariant; children: ReactNode };

export const Alert = ({ variant, children }: AlertProps) => (
  <div
    role="alert"
    className={cn('rounded-xl border px-4 py-3 text-sm leading-relaxed', ALERT_VARIANT[variant])}
  >
    {children}
  </div>
);

export const Skeleton = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('animate-pulse rounded-md bg-grey-200', className)} {...props} />
);
